import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatBytes } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, Activity, CheckCircle2, XCircle, HardDrive, ArrowRight, Inbox, Clock } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex h-full flex-col justify-between border border-border bg-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <p className="mt-6 font-heading text-4xl font-bold tracking-tighter">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [sub, setSub] = useState(null);

  const load = async () => {
    const [s, m, sb] = await Promise.all([api.get("/stats"), api.get("/migrations"), api.get("/me/subscription")]);
    setStats(s.data);
    setMigrations(m.data);
    setSub(sb.data);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Dashboard</p>
          <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">Overview</h1>
        </div>
        <Button className="gap-2" data-testid="new-migration-button" onClick={() => navigate("/new")}>
          <Plus className="h-4 w-4" /> New Migration
        </Button>
      </div>

      {sub && !sub.is_admin && (
        sub.subscription?.is_active ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 border border-primary/30 bg-accent px-5 py-3 text-sm" data-testid="plan-banner">
            <span><strong>{sub.subscription.package_name}</strong> plan · {sub.subscription.migrations_used}/{sub.subscription.max_migrations === -1 ? "∞" : sub.subscription.max_migrations} migrations used</span>
            <button onClick={() => navigate("/pricing")} className="font-medium text-primary hover:underline">Manage plan</button>
          </div>
        ) : (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800" data-testid="no-plan-banner">
            <span>You don't have an active plan. Purchase one to start migrating.</span>
            <button onClick={() => navigate("/pricing")} className="font-medium text-amber-900 underline">View plans</button>
          </div>
        )
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat icon={Activity} label="Active" value={stats?.active ?? "-"} accent="text-blue-600" />
        <Stat icon={Clock} label="Scheduled" value={stats?.scheduled ?? "-"} accent="text-violet-600" />
        <Stat icon={CheckCircle2} label="Completed" value={stats?.completed ?? "-"} accent="text-emerald-600" />
        <Stat icon={XCircle} label="Failed" value={stats?.failed ?? "-"} accent="text-red-600" />
        <Stat icon={HardDrive} label="Data Moved" value={stats ? formatBytes(stats.bytes_transferred) : "-"} accent="text-secondary" />
      </div>

      <div className="mt-10">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">Migrations</h2>

        {migrations.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-border bg-card py-20 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground" />
            <p className="mt-4 font-medium">No migrations yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Create a single migration or bulk-upload a CSV.</p>
            <Button className="mt-6 gap-2" onClick={() => navigate("/new")} data-testid="empty-new-migration-button">
              <Plus className="h-4 w-4" /> Start a migration
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {migrations.map((m) => {
              const finished = m.status === "completed" || m.status === "failed";
              const pct = finished ? 100 : (m.total_emails > 0 ? Math.round((m.migrated_emails / m.total_emails) * 100) : 0);
              return (
                <button
                  key={m.id}
                  data-testid={`migration-card-${m.id}`}
                  onClick={() => navigate(`/migrations/${m.id}`)}
                  className="group w-full overflow-hidden border border-border bg-card p-6 text-left transition-colors duration-200 hover:border-primary"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-semibold tracking-tight">{m.name}</h3>
                        <StatusBadge status={m.status} />
                      </div>
                      <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                        {m.source_email} <span className="text-primary">→</span> {m.dest_email}
                      </p>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
                  </div>

                  <div className="mt-5">
                    <div className="mb-1.5 flex flex-wrap justify-between gap-x-2 font-mono text-xs text-muted-foreground">
                      <span className="truncate">{m.migrated_emails} / {m.total_emails || "?"} emails · {m.migrated_folders}/{m.total_folders} folders</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full bg-primary transition-all duration-500 ${m.status === "running" ? "progress-active" : ""}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
